import { differenceInHours, format, isValid, parse } from 'date-fns';

export interface RelativeTimeFormatOptions {
  now?: Date;
}

/**
 * Parse a backend UTC timestamp into a `Date`, tolerating a missing trailing
 * `Z` (the API is not always consistent) and over-precise fractional seconds.
 */
function toDate(value: string | Date): Date {
  if (value instanceof Date) return value;
  const hasTimezone = value.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(value);
  const utcString = hasTimezone ? value : value + 'Z';
  return new Date(utcString.replace(/(\.\d{3})\d+/, '$1'));
}

export function parseUtc(value: string): Date {
  return toDate(value);
}

export function formatLocalDateTime(utcDate: string | Date): string {
  return format(toDate(utcDate), 'MMM d, yyyy, h:mm a');
}

export function formatLocalDate(utcDate: string | Date): string {
  return format(toDate(utcDate), 'MMM d, yyyy');
}

export function formatLocalTime(utcDate: string | Date): string {
  return format(toDate(utcDate), 'h:mm a');
}

/**
 * Below this, a four-digit year is a sentinel rather than a date: `DateTime.MinValue` serialises as
 * "0001-01-01T00:00:00", and no appointment or disclosed record in this system predates the year
 * 1900. Treating those as unparseable keeps an unset backend value out of a PHI audit table.
 */
const MIN_WALL_CLOCK_YEAR = 1900;

/**
 * Capture groups: 1 year, 2 month, 3 day, 4 hours, 5 minutes, 6 seconds. The time portion is
 * optional so a date-only value still yields a usable date; {@link formatWallClockTime} checks the
 * hours group rather than formatting the midnight the `Date` constructor substitutes for it.
 */
const WALL_CLOCK_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/;

/**
 * Parse a backend **wall-clock** timestamp — one that is already the local time at the facility
 * and carries no zone — without shifting it.
 *
 * The `formatLocal*` helpers above exist for UTC timestamps and go through {@link toDate}, which
 * appends a `Z` to a naked value and then renders it in the *browser's* zone. That is right for a
 * `…OnUtc` field and wrong for a value the backend has already localised: a 10:00 AM appointment
 * read on a UTC-4 machine would render as 6:00 AM. On an audit surface that is a silently
 * incorrect fact, not a cosmetic drift, so wall-clock values get their own parse.
 *
 * The date-part and time-part components are read off the string and handed to the `Date`
 * constructor as local components, which round-trips the wall-clock reading unchanged in every
 * zone. Returns an invalid `Date` for anything unparseable; callers format via
 * {@link formatWallClockDate} / {@link formatWallClockTime}, which render an empty string instead
 * of throwing.
 *
 * "Unparseable" is enforced strictly, because on an audit surface a *wrong* date is worse than no
 * date. The pattern is anchored at both ends and the result is checked against the components it
 * was built from, which rejects three ways the `Date` constructor would otherwise invent a value:
 *
 * - **Rollover.** `new Date(2026, 12, 45)` is not an error, it is February 2027. An out-of-range
 *   month or day therefore has to be caught after the fact, not by the pattern; an out-of-range
 *   hour or minute is range-checked up front instead (see the note at the check).
 * - **Two-digit years.** The constructor maps years 0-99 to 1900-1999, so an unset backend
 *   `DateTime` ("0001-01-01T00:00:00") would render as "Jan 1, 1901" — a plausible-looking date
 *   that was never in the record. `setFullYear` restores the literal year, and
 *   {@link MIN_WALL_CLOCK_YEAR} then rejects it as the sentinel it is rather than printing
 *   "Jan 1, 0001" into an appointment table.
 * - **A zone designator.** Anchoring rejects a trailing `Z` or `±hh:mm` instead of silently
 *   discarding it. If this field ever starts carrying a zone it is no longer wall-clock, and the
 *   caller needs the `formatLocal*` helpers — so that must surface as a blank cell to investigate,
 *   not as an unconverted instant that looks right.
 */
function toWallClockDate(value: string): Date {
  // Deliberately not `new Date(value)`: a naked "2026-07-14T10:00:00" is zone-dependent across
  // engines, and a date-only "2026-07-14" is parsed as UTC midnight by spec.
  const match = WALL_CLOCK_PATTERN.exec(value);
  if (!match) {
    return new Date(NaN);
  }
  const [, year, month, day, hours = '0', minutes = '0', seconds = '0'] = match;

  // Time components are range-checked rather than round-tripped: a wall-clock time that falls in a
  // DST spring-forward gap (02:30 on a shift day) is normalised forward by the constructor, and
  // blanking a real appointment over that would be its own wrong answer. The date parts are what
  // rollover actually corrupts.
  if (Number(hours) > 23 || Number(minutes) > 59 || Number(seconds) > 59) {
    return new Date(NaN);
  }

  if (Number(year) < MIN_WALL_CLOCK_YEAR) {
    return new Date(NaN);
  }

  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
    Number(seconds),
  );
  date.setFullYear(Number(year));
  const dateRoundTrips =
    date.getFullYear() === Number(year) &&
    date.getMonth() === Number(month) - 1 &&
    date.getDate() === Number(day);
  return dateRoundTrips ? date : new Date(NaN);
}

/**
 * Format the date part of a wall-clock timestamp, e.g. "Jul 14, 2026". See
 * {@link toWallClockDate} for why this is not `formatLocalDate`. Empty string when unparseable —
 * an audit row with an unreadable date shows nothing rather than "Invalid Date".
 */
export function formatWallClockDate(value: string): string {
  const date = toWallClockDate(value);
  return isValid(date) ? format(date, 'MMM d, yyyy') : '';
}

/**
 * Format the time part of a wall-clock timestamp, e.g. "10:00 AM". See {@link formatWallClockDate}.
 *
 * Empty string when unparseable **and** when the value carries no time part at all. A date-only
 * value parses fine — {@link toWallClockDate} defaults the missing components to zero, so the date
 * still renders — but formatting that as "12:00 AM" would state a time the record never carried,
 * which is the same wrong-fact-on-an-audit-trail this file exists to avoid. Callers render the
 * empty string as no subtext rather than as a placeholder.
 */
export function formatWallClockTime(value: string): string {
  const date = toWallClockDate(value);
  if (!isValid(date) || !WALL_CLOCK_PATTERN.exec(value)?.[4]) {
    return '';
  }
  return format(date, 'h:mm a');
}

export function formatDob(value: string): string {
  const date = new Date(value.includes('T') ? value : `${value}T00:00:00Z`);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

/**
 * Format a `DateOnly` (`yyyy-MM-dd`, optionally with a time component) as
 * `MM/dd/yyyy`. A plain string transform — no `Date` parsing — so a date-only
 * value can't drift across a timezone boundary.
 */
export function formatDobNumeric(value: string): string {
  // Anything shorter than `yyyy-MM-dd` can't carry a date. Without this an
  // empty value formats to the literal "undefined/undefined/", which is what a
  // staff member would then see pre-filled in a search field.
  if (!value || value.length < 10) {
    return '';
  }
  const [year, month, day] = value.slice(0, 10).split('-');
  return `${month}/${day}/${year}`;
}

// Shape check only — the pieces are read via split(), and calendar validity is
// date-fns' job, so this needs no capture groups.
const NUMERIC_DATE_PATTERN = /^\d{1,2}\/\d{1,2}\/\d{4}$/;

/**
 * True when `value` is a real `MM/DD/YYYY` calendar date (zero-padding
 * optional). Delegates to date-fns `parse`, which is month-length and
 * leap-year aware, so `02/31/1987` is rejected — not just `13/40/1990`.
 */
export function isNumericDate(value: string): boolean {
  if (!NUMERIC_DATE_PATTERN.test(value)) {
    return false;
  }
  return isValid(parse(value, 'M/d/yyyy', new Date(2000, 0, 1)));
}

/**
 * Convert a `MM/DD/YYYY` string (zero-padding optional) to the API's
 * `yyyy-MM-dd` shape. Returns `null` for empty or malformed input rather than
 * emitting a garbage date — callers should validate with {@link isNumericDate}
 * and surface an error instead of searching on a date the user didn't mean.
 */
export function numericDateToIso(value: string | null): string | null {
  if (!value || !isNumericDate(value)) {
    return null;
  }
  const [month, day, year] = value.split('/');
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

export function formatPhoneNumber(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const digits = value.replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return local.length === 10
    ? `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`
    : value;
}

export function formatRelativeTime(
  utcDate: string | Date,
  options: RelativeTimeFormatOptions = {},
): string {
  const target = toDate(utcDate);
  const now = options.now ?? new Date();
  const diffInHours = Math.max(0, differenceInHours(now, target));

  if (diffInHours < 24) {
    return `${diffInHours} hr${diffInHours === 1 ? '' : 's'} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
}
